import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown, Archive, Trash2, TrendingUp, Video } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../../components/ui/dropdown-menu';
import { cn } from '../../../../../lib/utils';

function ReviewActionsPanel({
  isAllApproved,
  isAllFinished,
  isAllApprovedWithPercentage,
  isLastLevel,
  isDisabled,
  onNextGoalClick,
  onArchive,
  onDelete,
  onAddRecordingLink,
}) {
  const [selectedAction, setSelectedAction] = useState('archive');

  useEffect(() => {
    if (isAllApproved || isAllApprovedWithPercentage) {
      setSelectedAction('nextGoal');
    } else {
      setSelectedAction('archive');
    }
  }, [isAllApproved, isAllApprovedWithPercentage]);

  const handlePrimaryAction = () => {
    switch (selectedAction) {
      case 'nextGoal':
        onNextGoalClick();
        break;
      case 'archive':
        onArchive();
        break;
      case 'delete':
        onDelete();
        break;
      case 'addLink':
        onAddRecordingLink();
        break;
      default:
        break;
    }
  };

  const getActionLabel = () => {
    switch (selectedAction) {
      case 'nextGoal':
        return 'Set Next Goal';
      case 'archive':
        return 'Archive Review';
      case 'delete':
        return 'Delete Review';
      case 'addLink':
        return 'Add Recording Link';
      default:
        return 'Select Action';
    }
  };

  const getActionIcon = () => {
    switch (selectedAction) {
      case 'nextGoal':
        return <TrendingUp className="h-4 w-4 mr-2" />;
      case 'archive':
        return <Archive className="h-4 w-4 mr-2" />;
      case 'delete':
        return <Trash2 className="h-4 w-4 mr-2" />;
      case 'addLink':
        return <Video className="h-4 w-4 mr-2" />;
      default:
        return null;
    }
  };

  const isPrimaryDisabled = () => {
    if (isDisabled) return true;
    if (selectedAction === 'archive' && !isAllFinished) return true;
    if (selectedAction === 'nextGoal' && !isAllApproved && !isAllApprovedWithPercentage)
      return true;
    return false;
  };

  return (
    <Card style={{ border: '1px solid hsl(var(--border))' }}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {isAllFinished && (
              <span className="text-green-600 font-medium">All skills have been reviewed.</span>
            )}
            {!isAllFinished && <span>Review all skills before completing this review.</span>}
          </div>

          <div className="flex items-center gap-2">
            <div className="flex">
              <Button
                onClick={handlePrimaryAction}
                disabled={isPrimaryDisabled()}
                className={cn(
                  'rounded-r-none',
                  selectedAction === 'delete' && 'bg-destructive hover:bg-destructive/90',
                )}
              >
                {getActionIcon()}
                {getActionLabel()}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant={selectedAction === 'delete' ? 'destructive' : 'default'}
                    className="rounded-l-none border-l border-l-white/20 px-2"
                    disabled={isDisabled}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setSelectedAction('addLink')}>
                    <Video className="h-4 w-4 mr-2" />
                    Add Recording Link
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setSelectedAction('archive')}
                    disabled={!isAllFinished}
                  >
                    <Archive className="h-4 w-4 mr-2" />
                    Archive Review
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setSelectedAction('nextGoal')}
                    disabled={(!isAllApproved && !isAllApprovedWithPercentage) || isLastLevel}
                  >
                    <TrendingUp className="h-4 w-4 mr-2" />
                    Set Next Goal
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setSelectedAction('delete')}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Review
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

ReviewActionsPanel.propTypes = {
  isAllApproved: PropTypes.bool.isRequired,
  isAllFinished: PropTypes.bool.isRequired,
  isAllApprovedWithPercentage: PropTypes.bool.isRequired,
  isLastLevel: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  onNextGoalClick: PropTypes.func.isRequired,
  onArchive: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onAddRecordingLink: PropTypes.func.isRequired,
};

export default ReviewActionsPanel;
